import { addMilliseconds, isSameMinute } from 'date-fns'
import { RruleHelper } from '../features/recurrence/rruleHelper'
import { DateRange, DateUtils } from '../services/date-utils.service'
import { RecurrentFeathersModel } from './RecurrentFeathersModel'
import { AssistantAvailability } from './assistantAvailability'

/**
 * Model representing an assistant's availability in list view
 * Handles recurring availabilities and time calculations
 */
export class ListAssistantAvailability extends RecurrentFeathersModel {
  static serviceName = 'features/assistant-availabilities-list/assistant-availabilities'
  private TAG = 'AssistantListAvailabilities'

  assistantId: number
  assistantFullName: string
  assistantFirstName: string
  assistantLastName: string
  plannedFrom: string
  plannedTo: string
  plannedDurationHours: number
  rrule: string
  recurrenceParentId: number
  recurrenceExceptionDate: Date
  recurrenceException: string
  contractTypeId: number
  note: string
  dateStart: Date
  timeStart: Date
  dateEnd: Date
  timeEnd: Date

  constructor(public id: number = null) {
    super(id)
  }

  /**
   * Generates the first occurrence of a recurring availability
   */
  generateFirstOccurrence() {
    const availability = ListAssistantAvailability.createFromProps(ListAssistantAvailability, this)
    availability.recurrenceParentId = null
    return availability
  }

  /**
   * Generates a new occurrence of a recurring availability
   * @param occurrenceStartDate Start date of the occurrence
   * @param occurrenceEndDate End date of the occurrence
   */
  generateOccurrence(occurrenceStartDate: Date, occurrenceEndDate: Date) {
    const availability = ListAssistantAvailability.createFromProps(ListAssistantAvailability, this)
    availability.id = null
    availability.plannedFrom = occurrenceStartDate.toISOString()
    availability.plannedTo = occurrenceEndDate.toISOString()
    availability.recurrenceParentId = this.id
    availability.recurrenceException = null
    availability.rrule = null
    availability.recurrenceExceptionDate = occurrenceStartDate
    return availability
  }

  /**
   * Calculates total duration of availabilities in decimal hours
   * @param availabilities Array of availabilities to sum
   * @param decimalPlaces Number of decimal places to round to
   */
  static sumDurationHHDecimalMinutes(availabilities: ListAssistantAvailability[], decimalPlaces = 2) {
    if (!availabilities) return '00.00'
    const durationMsTotal = availabilities.reduce((total, curr) => {
      return total + curr.durationMs
    }, 0)
    return DateUtils.millisecondsToHours(durationMsTotal).toFixed(2)
  }

  /**
   * Gets the start date of this availability
   */
  getDateFrom(): Date {
    return this.plannedFrom ? new Date(this.plannedFrom) : undefined
  }

  /**
   * Gets the end date of this availability
   */
  getDateTo(): Date {
    return this.plannedTo ? new Date(this.plannedTo) : undefined
  }

  public static expandAvailabilitiesByRrules(
    availabilities: ListAssistantAvailability[],
    range?: DateRange,
    expandParentsAsOccurrences = false,
  ) {
    const expanded: ListAssistantAvailability[] = []

    availabilities.forEach((aa: ListAssistantAvailability) => {
      if (aa.rrule) {
        expanded.push(...ListAssistantAvailability.expandAvailabilityByRrules(aa, range, expandParentsAsOccurrences))
      } else {
        expanded.push(aa)
      }
    })
    return expanded
  }

  private static expandAvailabilityByRrules(
    parent: ListAssistantAvailability,
    range?: DateRange,
    expandParentAsOccurrence = false,
  ) {
    const expanded: ListAssistantAvailability[] = []

    try {
      const occurenceStartDates: Date[] = RruleHelper.expandRecurrentFeathersModel(
        parent as RecurrentFeathersModel,
        range,
      )

      const parentStartDate = parent.getDateFrom()
      const parentDurationMs = parent.getDateTo().getTime() - parent.getDateFrom().getTime()

      occurenceStartDates.forEach((occurrenceStartDate) => {
        if (!expandParentAsOccurrence && isSameMinute(occurrenceStartDate, parentStartDate)) {
          // Means that current occurrence is the first occurrence in the series
          expanded.push(ListAssistantAvailability.generateFirstOccurrence(parent))
        } else {
          expanded.push(
            ListAssistantAvailability.generateOccurrence(
              parent,
              occurrenceStartDate,
              addMilliseconds(occurrenceStartDate, parentDurationMs),
            ),
          )
        }
      })

      return expanded
    } catch (e) {
      console.error(parent, e)
      return []
    }
  }

  static generateFirstOccurrence(parent: ListAssistantAvailability) {
    const availability = ListAssistantAvailability.createFromProps(ListAssistantAvailability, parent)
    availability.recurrenceParentId = null

    return availability
  }

  /**
   * Sets the start and end times of this availability
   * @param start Optional start date
   * @param end Optional end date
   */
  public setTimes(start?: Date, end?: Date) {
    if (start) {
      this.plannedFrom = start.toISOString()
    }
    if (end) {
      this.plannedTo = end.toISOString()
    }
    this.initTimes()
  }

  public initTimes() {
    if (this.plannedFrom) {
      this.timeStart = new Date(this.plannedFrom)
      this.dateStart = new Date(this.plannedFrom)
    }
    if (this.plannedTo) {
      this.timeEnd = new Date(this.plannedTo)
      this.dateEnd = new Date(this.plannedTo)
    }
  }

  static generateOccurrence(parent: ListAssistantAvailability, startDate: Date, endDate: Date) {
    const availability = ListAssistantAvailability.createFromProps(ListAssistantAvailability, parent)
    availability.id = null

    availability.setTimes(startDate, endDate)

    availability.recurrenceParentId = parent.id
    availability.recurrenceException = null
    availability.rrule = null
    availability.recurrenceExceptionDate = availability.getDateFrom()

    return availability
  }

  /**
   * Converts this ListAssistantAvailability to an AssistantAvailability
   */
  toAssistantAvailability(): AssistantAvailability {
    const availability = new AssistantAvailability(this.id)
    availability.dt_start = new Date(this.plannedFrom)
    availability.dt_end = new Date(this.plannedTo)
    availability.note = this.note
    availability.assistantId = this.assistantId
    availability.rrule = this.rrule
    availability.recurrenceParentId = this.recurrenceParentId
    availability.recurrenceException = this.recurrenceException
    availability.recurrenceExceptionDate = this.recurrenceExceptionDate ? new Date(this.recurrenceExceptionDate) : null
    return availability
  }
}
